import type { IProductBasic } from '@noths/polaris-client-listings-api';
import { isHighlyRated, isTopBrand } from '@noths/polaris-client-listings-api';
import { getProductImageURL } from '@noths/polaris-client-utils';
import type {
  ProductCardBadge,
  ProductCarouselItem,
} from '@noths/polaris-design-system-candidate-components';
import type { Product } from '@noths/polaris-design-system-ooux-interfaces';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { ServiceRecommendedProduct } from 'src/builders/recommendedProduct';

/**
 * What a card needs beyond the product itself: the currency the request quoted its
 * prices in, and whether the rail is one partner's products, which decides whether
 * a badge about the partner says anything.
 */
export interface RecommendationsCardOptions {
  currency: CurrencyCode;
  isSinglePartnerRail: boolean;
}

/**
 * Ordered by priority: the card has a single badge slot, so the first rule that
 * applies takes it. Same shape as the PDP's own `getProductBadge`.
 *
 * Most Loved comes first by product decision, and the data supports it. On 598
 * prod products, Top Brand is on 16% against Most Loved's 2%, so Most Loved is
 * much the more telling signal, and 43% of Most Loved products are also Top Brand,
 * meaning the other order would suppress nearly half of the Most Loved badges.
 *
 * `describesPartner` marks a badge that states a fact about the partner rather
 * than the product, which is what makes it worth nothing on a rail of one
 * partner's products. It sits on the rule to keep the reason next to what it
 * applies to.
 *
 * "Most Loved" and "Top Brand" are the customer-facing names for `highly_rated`
 * and `star_brand`, matching this page's own badge rules and the PLP's cards.
 */
const BADGE_RULES: {
  applies: (attributes: IProductBasic['attributes']) => boolean;
  badge: ProductCardBadge;
  describesPartner?: boolean;
}[] = [
  { applies: isHighlyRated, badge: { icon: 'mostLoved', label: 'Most Loved' } },
  {
    applies: isTopBrand,
    badge: { icon: 'topBrand', label: 'Top Brand' },
    describesPartner: true,
  },
];

/**
 * A skipped rule does not take the slot with it: the next rule that applies still
 * gets it, so a Most Loved product on the same-seller rail keeps its badge.
 */
const transformBadge = (
  { attributes }: ServiceRecommendedProduct,
  { isSinglePartnerRail }: Pick<RecommendationsCardOptions, 'isSinglePartnerRail'>,
): ProductCardBadge | undefined =>
  BADGE_RULES.find(
    ({ applies, describesPartner }) =>
      !(describesPartner && isSinglePartnerRail) && applies(attributes),
  )?.badge;

/**
 * The fields the recommendations GA events report on, and nothing else.
 *
 * The carousel item the design system renders is a semantic `Product`, which
 * deliberately carries none of these, so tracking reads the service product
 * directly rather than the shape on screen. Narrow rather than the whole product
 * so the dependency is visible and a spec only has to build what it asserts on.
 */
export type RecommendationsTrackingProduct = Pick<
  ServiceRecommendedProduct,
  | 'code'
  | 'freeDomesticDelivery'
  | 'isNew'
  | 'onSale'
  | 'partner'
  | 'prices'
  | 'purchasable'
  | 'salePercentage'
  | 'title'
>;

/**
 * Adapts a listings-api recommendation into the OOUX `Product` the design system
 * consumes.
 *
 * The interface is deliberately semantic, so several service fields have no
 * counterpart and are not mapped: `salePercentage` because `ProductCard` derives
 * the discount by comparing `price` with `originalPrice`, `onSale` because the
 * discount is the pre-sale price being present, and `freeDomesticDelivery`
 * because the card expresses delivery as a cost and an arrival date, which this
 * endpoint cannot supply (see
 * `transformRecommendedProductToProductCarouselItem`). All of them still reach
 * GA, which reads the service product directly.
 *
 * The rating and the badge are not part of the product either. They are card
 * presentation, so they sit on the carousel item alongside it.
 */
export const transformRecommendedProductToProduct = (
  {
    code,
    images,
    links,
    partner,
    preSalePrices,
    price,
    purchasable,
    title,
  }: ServiceRecommendedProduct,
  { currency }: { currency: CurrencyCode },
): Product => {
  const preSalePrice = preSalePrices?.find((preSale) => preSale.currency === price.currency);
  const [image] = images;

  return {
    /* Derived, not a constant. The request filters on the Vertex `availability`
       attribute rather than on `purchasable`, so nothing guarantees the two
       agree, and the card this replaces was given `purchasable` directly. */
    availability: purchasable ? 'in_stock' : 'out_of_stock',
    code: String(code),
    currencyCode: currency,
    /* The service returns a `{size}` token in place of a preset size, so the URL
       has to be resolved here: `ProductMedia.url` is contracted as a fully
       resolved URL and the card renders it verbatim, unlike the Ribbons card it
       replaces, which resolved the token itself. 'normal' is the size that card
       asked for, so the bytes on the wire do not change.

       Empty alt because the card wraps the image in a link named by the title,
       so describing it again would announce the product twice. */
    media: image ? [{ alt: '', type: 'image', url: getProductImageURL(image.href, 'normal') }] : [],
    /* Keyed on the pre-sale price alone, not on `onSale`. That matches the card
       this replaces, which took the pre-sale price and never received `onSale` at
       all, so a product priced down without the flag set keeps showing its
       discount. */
    ...(preSalePrice && { originalPrice: preSalePrice.amount }),
    // The card shows the partner's name as plain text. `shortcode` and `url` are
    // optional and unrendered, so they are left out until something needs them.
    partner: { name: partner.name },
    /* The single `price` field, not a lookup in `prices`. The request sets the
       currency, so this one is already quoted in it, which is what the adapter
       this replaces relied on. */
    price: price.amount,
    title,
    /* `rel`, not `href`. The recommendations response puts the site-relative
       product path in `rel` and an absolute URL in `href` that points at a QA
       host outside production. */
    url: links[0]?.rel || '',
  };
};

/**
 * Gated on the average being above zero rather than on `ratings` being present.
 * An average of zero means unrated rather than rated zero, and would otherwise
 * render an empty star row where the card shows "NEW". Matches the PLP's own
 * `hasRating` rule.
 *
 * `ratingCount` is deliberately unused: the card shows the average alone.
 */
const transformRating = ({ ratings }: ServiceRecommendedProduct): number | undefined =>
  typeof ratings?.averageRating === 'number' &&
  Number.isFinite(ratings.averageRating) &&
  ratings.averageRating > 0
    ? ratings.averageRating
    : undefined;

/**
 * One carousel item is one card's props: the product itself plus the flags that
 * are presentation rather than properties of the product.
 *
 * `priority` is deliberately unset. The carousel is client-fetched behind
 * `LazyRender`, so it is never above the fold and its images should not pre-empt
 * ones that are.
 *
 * The badge follows `BADGE_RULES`, except that a single-partner rail skips Top
 * Brand. It describes the partner rather than the product, so on "Our bestsellers"
 * it would be on every card or none, directly below a `PartnerMaxiModule` that
 * already says Top Brand. The partner page's own bestsellers rail in
 * browse-productlistings drops it for the same reason, by omission rather than by
 * flag, since that surface has no mixed rail.
 *
 * `isSinglePartnerRail` is required rather than defaulted, so a rail added later
 * has to say which kind it is rather than quietly badging.
 *
 * No `delivery`, and this is a deliberate omission rather than a gap waiting to be
 * filled. The recommendations endpoint returns `delivery_cost` and `delivery_days`
 * but not the type suffixed attributes, and bare `delivery_days` is not an arrival
 * lead time: measured on prod it sits below the fastest real delivery option on
 * 220 of 299 products, by 1 to 4 days. Dating a card from it put the arrival 1 to
 * 4 days earlier than the PLP grid's own card for the same product on 74% of
 * products, promising a date no delivery option achieves. A card needs
 * `delivery_days_FREE` or `delivery_days_STANDARD`, which this endpoint does not
 * return, so until it does, or until the filter-aware resolution moves server
 * side, there is no delivery line here. See `IRecommendationsAttributes`.
 */
export const transformRecommendedProductToProductCarouselItem = (
  product: ServiceRecommendedProduct,
  { currency, isSinglePartnerRail }: RecommendationsCardOptions,
): ProductCarouselItem => {
  const badge = transformBadge(product, { isSinglePartnerRail });
  const rating = transformRating(product);

  return {
    ...(badge && { badge }),
    /* The card ignores `isNew` once a rating is given, showing the rating in the
       slot the "NEW" label would occupy, so both are passed and the card chooses. */
    isNew: product.isNew,
    product: transformRecommendedProductToProduct(product, { currency }),
    /* Constant, not `product.onSale`. Every discount the catalog expresses is a
       price reduction rather than a code-based offer, and the card this replaces
       styled all of them as a sale, so there is no per-product decision to make
       here. `promoteAsSale` does nothing on a product with no discount, so it does
       not need guarding. */
    promoteAsSale: true,
    ...(rating !== undefined && { rating }),
  };
};
